function clamp(value, min, max) {
	return Math.min(Math.max(value, min), max);
}

function clearInlineStyles(els) {
	els.forEach((el) => {
		if (!el || !el.style) return;
		el.style.removeProperty('transform');
		el.style.removeProperty('opacity');
		el.style.removeProperty('pointer-events');
		el.style.removeProperty('z-index');
	});
}

function prefersReducedMotion() {
	return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default {
	init() {
		const stages = document.querySelectorAll('.layout.hero-scroll [data-hero-scroll]');
		if (!stages.length) return;

		stages.forEach((stageEl) => {
			if (stageEl._heroScrollInited) return;
			stageEl._heroScrollInited = true;

			const scrollEl = stageEl.querySelector('.hero-scroll-scroll');
			if (!scrollEl) return;

			const copyItems = Array.from(
				stageEl.querySelectorAll('[data-hero-scroll-item="copy"]')
			);
			const imageItems = Array.from(
				stageEl.querySelectorAll('[data-hero-scroll-item="image"]')
			);
			const indexNumbers = Array.from(stageEl.querySelectorAll('[data-hero-scroll-index]'));
			const steps = Math.max(copyItems.length, imageItems.length);
			if (steps <= 1) return;

			const mql = window.matchMedia('(min-width: 769px)');
			let rafId = null;
			let enhanced = false;
			let currentIndex = 0;

			function setActiveIndex(idx) {
				currentIndex = idx;
				indexNumbers.forEach((el) => {
					const i = parseInt(el.getAttribute('data-hero-scroll-index') || '', 10);
					const active = i === idx;
					el.classList.toggle('is-active', active);
				});
			}

			function scrollToIndex(targetIndex, opts = {}) {
				const idx = clamp(targetIndex, 0, steps - 1);
				const max = steps - 1;
				if (max <= 0) return;

				const scrollRect = scrollEl.getBoundingClientRect();
				const scrollTopAbs = window.scrollY + scrollRect.top;
				const total = scrollEl.offsetHeight - window.innerHeight;
				const offset = clamp(total, 0, Number.MAX_SAFE_INTEGER);
				const t = idx / max;
				const top = scrollTopAbs + offset * t;
				const behavior =
					opts && typeof opts === 'object' && opts.behavior
						? opts.behavior
						: prefersReducedMotion()
							? 'auto'
							: 'smooth';

				window.scrollTo({
					top,
					behavior,
				});
			}

			function getIndexFromCopyItemEl(copyItemEl) {
				if (!copyItemEl) return null;
				const raw = copyItemEl.getAttribute('data-hero-scroll-index');
				const idx = parseInt(raw || '', 10);
				return Number.isFinite(idx) ? idx : null;
			}

			function update() {
				if (!enhanced) return;

				const rect = scrollEl.getBoundingClientRect();
				const total = rect.height - window.innerHeight;
				const scrolled = clamp(-rect.top, 0, Math.max(1, total));
				const p = total > 0 ? (scrolled / total) * (steps - 1) : 0;

				const base = Math.floor(p);
				const frac = p - base;

				// Keep copy visible until the very end of the step,
				// then fade out/in quickly to the next hero.
				const FADE_WINDOW = 0.15;
				const fadeStart = 1 - FADE_WINDOW;
				const fadeT = frac <= fadeStart ? 0 : clamp((frac - fadeStart) / FADE_WINDOW, 0, 1);

				const activeIndex = clamp(
					frac >= fadeStart + FADE_WINDOW / 2 ? base + 1 : base,
					0,
					steps - 1
				);
				setActiveIndex(activeIndex);

				imageItems.forEach((imgEl, i) => {
					const t = clamp(p - i, 0, 1);
					imgEl.style.transform = `translate3d(0, ${-100 * t}%, 0)`;
					imgEl.style.zIndex = String(steps - i);
				});

				copyItems.forEach((copyEl, i) => {
					let opacity = 0;
					if (i === base) opacity = 1 - fadeT;
					if (i === base + 1) opacity = fadeT;

					copyEl.style.opacity = String(clamp(opacity, 0, 1));
					copyEl.style.pointerEvents = opacity > 0.5 ? 'auto' : 'none';
				});
			}

			function onScrollOrResize() {
				if (!enhanced) return;
				if (rafId) return;
				rafId = window.requestAnimationFrame(() => {
					rafId = null;
					update();
				});
			}

			function enable() {
				if (enhanced) return;
				enhanced = true;
				stageEl.classList.add('is-enhanced');
				update();
				window.addEventListener('scroll', onScrollOrResize, { passive: true });
				window.addEventListener('resize', onScrollOrResize, { passive: true });
			}

			function disable() {
				if (!enhanced) return;
				enhanced = false;
				stageEl.classList.remove('is-enhanced');
				window.removeEventListener('scroll', onScrollOrResize);
				window.removeEventListener('resize', onScrollOrResize);
				if (rafId) {
					window.cancelAnimationFrame(rafId);
					rafId = null;
				}
				clearInlineStyles(copyItems);
				clearInlineStyles(imageItems);
				setActiveIndex(0);
			}

			function sync() {
				if (mql.matches) enable();
				else disable();
			}

			// Initial + responsive.
			sync();
			if (mql.addEventListener) {
				mql.addEventListener('change', sync);
			} else if (mql.addListener) {
				mql.addListener(sync);
			}

			// Accessibility: when tab focus lands on a hero's CTAs,
			// scroll the scrubber to that hero so the focused element is visible.
			stageEl.addEventListener(
				'focusin',
				(e) => {
					if (!enhanced) return;
					const target = e.target;
					if (!(target instanceof Element)) return;

					const copyItemEl = target.closest('.hero-scroll-copy-item');
					if (!copyItemEl || !stageEl.contains(copyItemEl)) return;

					const idx = getIndexFromCopyItemEl(copyItemEl);
					if (idx === null) return;
					if (idx === currentIndex) return;

					// Use an instant jump for keyboard navigation; smooth can be disorienting.
					scrollToIndex(idx, { behavior: 'auto' });
					// Ensure state updates even if the browser coalesces scroll events.
					onScrollOrResize();
				},
				true
			);
		});
	},
};
